"use client";

import { LiquidPillGroup, Modal, type LiquidPillGroupItem } from "components/ui/v2";
import useTranslation from "next-translate/useTranslation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ExploreReactionsModalContentV2 } from "./ExploreReactionsModalContentV2";

export interface ExploreReactionsModalV2Props {
  open: boolean;
  onClose: () => void;
  id: string;
  type: "message" | "comment";
}

type HeaderTabsState = {
  items: LiquidPillGroupItem[];
  active: string;
  setActive: (val: string) => void;
};

export function ExploreReactionsModalV2({
  open,
  onClose,
  id,
  type,
}: ExploreReactionsModalV2Props) {
  const { t } = useTranslation("common");
  const [headerTabs, setHeaderTabs] = useState<HeaderTabsState | null>(null);
  const [reactionCount, setReactionCount] = useState<number | null>(null);
  const [contentLoading, setContentLoading] = useState(true);

  useEffect(() => {
    if (open) {
      setContentLoading(true);
      setHeaderTabs(null);
      setReactionCount(null);
    }
  }, [open, id, type]);

  const handleClose = useCallback(() => {
    // Don't reset loading here — Modal stays mounted during the exit
    // animation; flipping loading would flash the compact spinner.
    // Reset happens on the next open via the effect above.
    onClose();
  }, [onClose]);

  const handleLoadingChange = useCallback((loading: boolean) => {
    // One-way: only clear. Refetch must not reopen the compact shell.
    if (!loading) {
      setContentLoading(false);
    }
  }, []);

  const description = useMemo(() => {
    if (contentLoading || reactionCount === null) return undefined;
    if (reactionCount === 0) return t("Sin reacciones todavía");
    if (reactionCount === 1) return t("1 reacción");
    return t("REACTIONS_COUNT", { count: reactionCount });
  }, [contentLoading, reactionCount, t]);

  const handleTabChange = useCallback(
    (
      items: LiquidPillGroupItem[],
      active: string,
      setActive: (val: string) => void
    ) => {
      setHeaderTabs((prev) => {
        if (
          prev &&
          prev.active === active &&
          prev.items.length === items.length
        ) {
          return prev;
        }
        return { items, active, setActive };
      });
    },
    []
  );

  const header = (
    <div className="flex w-full items-center justify-between gap-4">
      <div className="min-w-0 flex-1">
        <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-indigo-500/90 dark:text-indigo-300/80">
          {t("Explore")}
        </p>
        <h2 className="mt-0.5 text-[13px] font-semibold leading-snug text-slate-900 dark:text-zinc-50 sm:text-[15px]">
          {t("Reactions")}
        </h2>
        {description ? (
          <p className="mt-1 max-w-2xl text-[11px] text-slate-500 dark:text-zinc-400 sm:text-xs">
            {description}
          </p>
        ) : null}
      </div>
      {headerTabs ? (
        <div className="mr-8 shrink-0 rounded-full border border-slate-200/40 bg-white/40 p-0.5 shadow-[0_1px_2px_rgba(15,23,42,0.02)] backdrop-blur-[2px] dark:border-zinc-800/40 dark:bg-zinc-900/40">
          <LiquidPillGroup
            items={headerTabs.items}
            activeValue={headerTabs.active}
            onChange={headerTabs.setActive}
            size="sm"
          />
        </div>
      ) : null}
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="xl"
      backdrop="static"
      title={t("Reactions")}
      header={header}
      panelClassName="flex max-h-[min(88vh,780px)] flex-col"
      headerClassName="px-4 py-2.5 sm:px-5 sm:py-3"
      bodyScrollFade
      bodyClassName="py-3 sm:py-3.5"
      bubbleReveal
      loading={contentLoading}
      loadingLabel={t("Cargando reacciones")}
    >
      <ExploreReactionsModalContentV2
        id={id}
        type={type}
        hideHeader
        onLoadingChange={handleLoadingChange}
        onTabChange={handleTabChange}
        onCountChange={setReactionCount}
      />
    </Modal>
  );
}

export default ExploreReactionsModalV2;
